import { z } from 'zod';
import { CLAVES_PERMISO, CLAVES_ROL, PLATAFORMAS } from './comunes';

/**
 * Política de contraseñas.
 *
 * Mínimo 10 caracteres y se rechazan las más comunes. A propósito NO se exige
 * mayúscula + número + símbolo: esa regla produce `Copathy2026!` en todas las
 * cuentas de la empresa, que es peor que una frase larga.
 */
export const LARGO_MINIMO_CONTRASENA = 10;

const CONTRASENAS_COMUNES = [
  '1234567890',
  'contrasena',
  'contraseña',
  'password12',
  'qwertyuiop',
  'copathy123',
  'administrador',
];

export const esquemaContrasena = z
  .string()
  .min(LARGO_MINIMO_CONTRASENA, `La contraseña debe tener al menos ${LARGO_MINIMO_CONTRASENA} caracteres.`)
  .max(200, 'La contraseña es demasiado larga.')
  .refine(
    (v) => !CONTRASENAS_COMUNES.includes(v.toLowerCase()),
    'Esa contraseña es demasiado común. Escoge otra.',
  )
  .refine((v) => v.trim().length > 0, 'La contraseña no puede ser solo espacios.');

export const esquemaCorreo = z
  .string()
  .trim()
  .min(1, 'El correo es obligatorio.')
  .max(254)
  .email('El correo no tiene un formato válido.')
  .transform((v) => v.toLowerCase());

export const esquemaLogin = z.object({
  correo: esquemaCorreo,
  // En el login no se valida la política: se compara contra el hash guardado.
  // Aplicarla acá le diría a un atacante cuál es la política antes de entrar.
  contrasena: z.string().min(1, 'La contraseña es obligatoria.').max(200),
});
export type EntradaLogin = z.infer<typeof esquemaLogin>;

export const esquemaRefresh = z.object({
  refreshToken: z.string().min(1).max(500).optional(),
});
export type EntradaRefresh = z.infer<typeof esquemaRefresh>;

export const esquemaActivar = z.object({
  token: z.string().min(20).max(200),
  contrasena: esquemaContrasena,
});
export type EntradaActivar = z.infer<typeof esquemaActivar>;

export const esquemaRecuperar = z.object({
  correo: esquemaCorreo,
});
export type EntradaRecuperar = z.infer<typeof esquemaRecuperar>;

export const esquemaRestablecer = z.object({
  token: z.string().min(20).max(200),
  contrasena: esquemaContrasena,
});
export type EntradaRestablecer = z.infer<typeof esquemaRestablecer>;

export const esquemaCambiarContrasena = z.object({
  contrasenaActual: z.string().min(1).max(200),
  contrasenaNueva: esquemaContrasena,
});
export type EntradaCambiarContrasena = z.infer<typeof esquemaCambiarContrasena>;

export const esquemaUsuarioSesion = z.object({
  id: z.number().int().positive(),
  nombre: z.string(),
  correo: z.string(),
  empresaId: z.number().int().positive(),
  empresaNombre: z.string(),
  rol: z.enum(CLAVES_ROL),
  debeCambiarContrasena: z.boolean(),
});
export type UsuarioSesion = z.infer<typeof esquemaUsuarioSesion>;

export const esquemaRespuestaSesion = z.object({
  accessToken: z.string(),
  /** Solo viaja en el cuerpo cuando el cliente es móvil. En web va en cookie httpOnly. */
  refreshToken: z.string().optional(),
  usuario: esquemaUsuarioSesion,
  permisos: z.array(z.enum(CLAVES_PERMISO)),
});
export type RespuestaSesion = z.infer<typeof esquemaRespuestaSesion>;

export const esquemaPlataforma = z.enum(PLATAFORMAS);

/** Contenido del access token. No lleva permisos: se resuelven en el servidor. */
export const esquemaContenidoToken = z.object({
  sub: z.number().int().positive(),
  empresaId: z.number().int().positive(),
  rol: z.enum(CLAVES_ROL),
  /** Verdadero solo para roles de ámbito Copathy. Habilita el cruce de frontera auditado. */
  cruzaFrontera: z.boolean(),
});
export type ContenidoToken = z.infer<typeof esquemaContenidoToken>;
